/**
 * NotificationsInbox — Iter 161.1
 *
 * The FocusHeaderChip's bell (and any deep-link with `unread`) lands
 * here. Renders the caller's in-app notification feed — role-agnostic
 * so admins, clinicians, planners, patients, and payers can all see
 * what's been queued for them.
 *
 * Uses the existing GET /api/notifications endpoint which already
 * returns per-user rows sorted by created_at desc.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bell, BellOff, Check, CheckCheck, Loader2, ArrowRight, Inbox,
} from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const homeHref = (role) => ({
  admin: '/admin/dashboard',
  clinician: '/provider-os',
  agency: '/provider-os',
  discharge_planner: '/planner/dashboard',
  hospital_admin: '/planner/dashboard',
  payer: '/payer/dashboard',
  patient: '/patient/dashboard',
}[role] || '/');

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const dt = new Date(iso);
    const diff = (Date.now() - dt.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return dt.toLocaleDateString();
  } catch {
    return '';
  }
};

const iconFor = (kind) => {
  const cls = 'w-4 h-4';
  if (!kind) return <Bell className={cls} />;
  if (/(offer|referral|placement)/i.test(kind)) return <Inbox className={cls} />;
  if (/(mfa|security|password)/i.test(kind)) return <BellOff className={cls} />;
  return <Bell className={cls} />;
};

export default function NotificationsInbox() {
  const { token, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications?limit=50`,
        { headers: { Authorization: `Bearer ${token}` } });
      setRows(data.notifications || []);
      setUnread(Number(data.unread_count) || 0);
    } catch (e) {
      toast.error('Could not load notifications');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    if (!token || saving || unread === 0) return;
    setSaving(true);
    try {
      await axios.post(`${API}/notifications/read-all`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Marked all as read');
      await load();
    } catch {
      toast.error('Could not mark all read');
    } finally {
      setSaving(false);
    }
  };

  const markOneRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/read`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setRows((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnread((u) => Math.max(0, u - 1));
    } catch {
      // Non-critical — leave item as unread and let the next reload fix it.
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-50 pb-12"
      data-testid="notifications-inbox"
    >
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-slate-700" />
              <h1 className="text-lg font-semibold text-slate-900" data-testid="notifications-inbox-title">
                Notifications
              </h1>
              {unread > 0 && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full bg-rose-600 text-white font-semibold"
                  data-testid="notifications-unread-count"
                >
                  {unread} new
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Referrals, security events, and platform updates for your account.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={homeHref(user?.role)}
              className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
              data-testid="notifications-back-home"
            >
              <ArrowRight className="w-3.5 h-3.5 rotate-180" /> Back
            </Link>
            <button
              type="button"
              onClick={markAllRead}
              disabled={saving || unread === 0}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-slate-900 text-white disabled:opacity-40"
              data-testid="notifications-mark-all-read"
            >
              {saving
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <CheckCheck className="w-3 h-3" />}
              Mark all read
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 mt-6">
        {loading ? (
          <div
            className="flex items-center justify-center py-16 text-slate-500 text-sm"
            data-testid="notifications-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div
            className="text-center py-16 rounded-2xl border border-dashed border-slate-200 bg-white"
            data-testid="notifications-empty"
          >
            <BellOff className="w-8 h-8 mx-auto text-slate-300 mb-3" />
            <div className="text-sm font-medium text-slate-800">You’re all caught up</div>
            <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
              We’ll drop referral offers, appointment reminders, and security
              events here as they happen.
            </p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="notifications-list">
            {rows.map((n) => {
              const cta = n.cta_url || n.link || null;
              const isUnread = !n.read;
              return (
                <li
                  key={n.id}
                  className={
                    'flex items-start gap-3 rounded-xl border p-4 bg-white ' +
                    (isUnread ? 'border-rose-200' : 'border-slate-200')
                  }
                  data-testid="notification-row"
                >
                  <div className={
                    'rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 ' +
                    (isUnread ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-500')
                  } aria-hidden>
                    {iconFor(n.kind || n.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-sm font-medium text-slate-900">
                        {n.title || n.subject || 'Update'}
                      </div>
                      <span
                        className="text-[10px] uppercase tracking-wider text-slate-400 flex-shrink-0"
                        title={n.created_at}
                      >
                        {formatTime(n.created_at)}
                      </span>
                    </div>
                    {n.body || n.message ? (
                      <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-line">
                        {n.body || n.message}
                      </p>
                    ) : null}
                    <div className="flex items-center gap-3 mt-2">
                      {cta && (
                        <Link
                          to={cta}
                          className="inline-flex items-center gap-1 text-xs text-slate-800 hover:text-slate-950 font-medium"
                          onClick={() => isUnread && markOneRead(n.id)}
                          data-testid="notification-row-cta"
                        >
                          Open <ArrowRight className="w-3 h-3" />
                        </Link>
                      )}
                      {isUnread && (
                        <button
                          type="button"
                          onClick={() => markOneRead(n.id)}
                          className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800"
                          data-testid="notification-row-mark-read"
                        >
                          <Check className="w-3 h-3" /> Mark read
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
